/**
 * ReportIssueButton — floating bottom-right helper on authenticated views.
 *
 * Opens support email with session context + last Sentry event ID (if any),
 * so support@juuniai.com can triage without asking for repro details.
 *
 * Visibility rules:
 *   - Only rendered when user is authenticated (parent layouts handle that)
 *   - Hidden on legal/signup/login routes (no app context to share)
 *   - Kept subtle (low-opacity at rest, fades in on hover)
 */

import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { buildSupportMailto } from '../utils/supportMailto';
import { getLastSentryEventId } from '../lib/sentry';

export default function ReportIssueButton() {
  const { user, effectiveRole, orgId } = useAuth();
  const location = useLocation();

  // Not shown when unauthed or on non-app routes
  if (!user) return null;
  if (/^\/(login|signup|legal|sla|__verify)/.test(location.pathname)) return null;

  const mailto = buildSupportMailto({
    orgId,
    role: effectiveRole,
    path: location.pathname,
    subject: 'Juuni — reporting an issue',
    sentryEventId: getLastSentryEventId(),
    intro: "Hey — something looks off. Here's what happened:\n\n(describe what you were trying to do and what happened instead)",
  });

  return (
    <a
      href={mailto}
      title="Report an issue — includes session context for fast triage"
      aria-label="Report an issue"
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 40,
        padding: '8px 14px',
        background: 'rgba(10,10,10,0.82)',
        color: '#fff',
        fontSize: 12,
        fontFamily: "'Inter', -apple-system, sans-serif",
        fontWeight: 500,
        borderRadius: 20,
        textDecoration: 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        opacity: 0.7,
        transition: 'opacity 0.2s, transform 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = '1';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = '0.7';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <span style={{ fontSize: 11, opacity: 0.85 }}>✉</span>
      Report issue
    </a>
  );
}
